import React, { useEffect, useState, useCallback, useRef } from "react";
import Layout from "../Layout";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { BaseUrl } from "../BaseUrl";

import AccountSettings from "./ProfileSettings/AccountSettings";
import PrivacySettings from "./ProfileSettings/PrivacySettings";
import DisplaySettings from "./ProfileSettings/DisplaySettings";
import EmailNotificationsSettings from "./ProfileSettings/EmailNotificationsSettings";
import LanguagesSettings from "./ProfileSettings/LanguagesSettings";
import SubscriptionsBillingSettings from "./ProfileSettings/SubscriptionsBillingSettings";

export default function Settings() {
  // Facility State
  const [profile, setProfile] = useState({
    _id: "",
    businessName: "",
    fullName: "",
    emailId: "",
    mobileNumber: "",
    addressLineNo1: "",
    addressLineNo2: "",
    cityDistrict: "",
    state: "",
    pincode: "",
    gstNo: "",
    panNo: "",
    facilityImage: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [activeSection, setActiveSection] = useState("Edit Profile");
  const [selectedLanguage, setSelectedLanguage] = useState("English");

  // Placeholders for other settings
  const [emailVerificationRequired, setEmailVerificationRequired] =
    useState(false);
  const [privacySettings, setPrivacySettings] = useState({});
  const [displaySettings, setDisplaySettings] = useState({});
  const [emailNotifications, setEmailNotifications] = useState({});

  const fileInputRef = React.useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Navigation logic
  useEffect(() => {
    const sectionFromState = location.state?.activeSection;
    const searchParams = new URLSearchParams(location.search);
    const sectionFromQuery = searchParams.get("section");

    if (sectionFromState) {
      setActiveSection(sectionFromState);
    } else if (sectionFromQuery) {
      setActiveSection(sectionFromQuery);
    }
  }, [location]);

  const handleBackClick = () => {
    navigate(-1);
  };

  // Fetch Profile Data - Use /doctor/me to get current authenticated user
  const fetchProfile = async () => {
    setIsLoading(true);
    try {
      // Get current user from token via /doctor/me endpoint
      const meResponse = await axios.get(`${BaseUrl}/doctor/me`, {
        withCredentials: true,
      });

      // console.log("� Settings - /doctor/me response:", meResponse.data);

      if (meResponse.data?.success && meResponse.data?.data) {
        const data = meResponse.data.data;

        // Store the user ID in localStorage for future use
        if (data._id) {
          localStorage.setItem("id", data._id);
        }

        // console.log("📋 Settings - Profile data received:", {
        //   _id: data._id,
        //   emailId: data.emailId,
        //   fullName: data.fullName,
        //   businessName: data.businessName,
        //   facilityImage: data.facilityImage,
        // });

        setProfile({
          _id: data._id, // Store ID for save operations
          businessName: data.businessName || "",
          fullName: data.fullName || "",
          emailId: data.emailId || "",
          mobileNumber: data.mobileNumber || "",
          addressLineNo1: data.addressLineNo1 || "",
          addressLineNo2: data.addressLineNo2 || "",
          cityDistrict: data.cityDistrict || "",
          state: data.state || "",
          pincode: data.pincode || "",
          gstNo: data.gstNo || "",
          panNo: data.panNo || "",
          facilityImage: data.facilityImage || "",
          // Privacy settings for Privacy tab
          privacySettings: data.privacySettings || {
            allowSearchEngineIndex: true,
            allowProfileDiscoveryByEmail: true,
            whoCanSendMessage: "anyone",
          },
        });

        // Set image preview if exists
        if (data.facilityImage) {
          setImagePreviewUrl(data.facilityImage);
        }
      } else {
        toast.error("Could not load profile");
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      console.error("❌ Error details:", error.response?.data);
      toast.error("Could not load profile. Please login again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  // Handle Input Changes
  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  // Handle Image Selection
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setProfile({ ...profile, imageFile: file }); // Store file for upload

    // Create preview
    const objectUrl = URL.createObjectURL(file);
    setImagePreviewUrl(objectUrl);
  };

  // Cleanup preview URL
  useEffect(() => {
    return () => {
      if (imagePreviewUrl && imagePreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  // Handle Save (Profile Update + Image Upload)
  const handleSave = async () => {
    setIsUploadingImage(true);
    try {
      const userId = profile._id || localStorage.getItem("id");
      if (!userId) {
        toast.error("User ID not found. Please refresh and try again.");
        return;
      }
      console.log("💾 Saving profile for userId:", userId);

      const formData = new FormData();
      // Append all text fields
      formData.append("businessName", profile.businessName);
      formData.append("fullName", profile.fullName);
      formData.append("emailId", profile.emailId);
      formData.append("mobileNumber", profile.mobileNumber);
      formData.append("addressLineNo1", profile.addressLineNo1);
      formData.append("addressLineNo2", profile.addressLineNo2);
      formData.append("cityDistrict", profile.cityDistrict);
      formData.append("state", profile.state);
      formData.append("pincode", profile.pincode);
      formData.append("gstNo", profile.gstNo);
      formData.append("panNo", profile.panNo);

      // Append file if selected
      if (profile.imageFile) {
        formData.append("file", profile.imageFile);
      }

      const response = await axios.put(
        `${BaseUrl}/facility/facilityProfile/update/${userId}`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data?.success || response.status === 200) {
        toast.success("Profile updated successfully!");
        // Update local state with returned data (especially for image URL)
        if (response.data.data) {
          const newData = response.data.data;
          setProfile((prev) => ({
            ...prev,
            facilityImage: newData.facilityImage,
            imageFile: null, // Clear pending file
          }));
          // If a new image was returned, ensure preview is updated to the remote URL
          if (newData.facilityImage) {
            setImagePreviewUrl(newData.facilityImage);
          }
        }
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error.response?.data?.message || "Update failed");
    } finally {
      setIsUploadingImage(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className='flex min-h-screen items-center justify-center bg-white p-8'>
          <div className='text-lg'>Loading profile...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className='min-h-screen bg-white'>
        <div className='flex flex-col md:flex-row'>
          {/* Left Sidebar */}
          <div className='min-h-screen w-full border-r border-gray-200 bg-white p-6 md:w-64'>
            <button
              onClick={handleBackClick}
              className='mb-6 flex items-center gap-2 font-semibold text-gray-600 hover:text-gray-900'
            >
              ← Back
            </button>

            {/* Profile Settings Section */}
            <div className='mb-8'>
              <h2 className='mb-4 border-b border-gray-200 pb-2 text-sm font-semibold uppercase tracking-wide text-gray-500'>
                Profile Settings
              </h2>
              <div className='space-y-2'>
                <button
                  onClick={() => setActiveSection("Edit Profile")}
                  className={`w-full rounded-md px-3 py-2 text-left font-medium transition ${
                    activeSection === "Edit Profile"
                      ? "bg-[#0095D9] bg-opacity-10 text-[#0095D9]"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  Edit Profile
                </button>
              </div>
            </div>

            {/* Other Settings (Placeholders) */}
            <div>
              <h2 className='mb-4 border-b border-gray-200 pb-2 text-sm font-semibold uppercase tracking-wide text-gray-500'>
                Settings
              </h2>
              {/* Simplified Sidebar Buttons */}
              <div className='space-y-2'>
                <button
                  onClick={() => setActiveSection("Account")}
                  className={`w-full rounded-md px-3 py-2 text-left font-medium transition ${
                    activeSection === "Account"
                      ? "bg-[#0095D9] bg-opacity-10 text-[#0095D9]"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  Account
                </button>
                <button
                  onClick={() => setActiveSection("Privacy")}
                  className={`w-full rounded-md px-3 py-2 text-left font-medium transition ${
                    activeSection === "Privacy"
                      ? "bg-[#0095D9] bg-opacity-10 text-[#0095D9]"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  Privacy
                </button>
                <button
                  onClick={() => setActiveSection("Email & Notifications")}
                  className={`w-full rounded-md px-3 py-2 text-left font-medium transition ${
                    activeSection === "Email & Notifications"
                      ? "bg-[#0095D9] bg-opacity-10 text-[#0095D9]"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  Email & Notifications
                </button>
                {/* Languages section - commented out for now
                <button
                  onClick={() => setActiveSection("Languages")}
                  className={`w-full rounded-md px-3 py-2 text-left font-medium transition ${
                    activeSection === "Languages"
                      ? "bg-[#0095D9] bg-opacity-10 text-[#0095D9]"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  Languages
                </button>
                */}
                <button
                  onClick={() => setActiveSection("Subscriptions & Billing")}
                  className={`w-full rounded-md px-3 py-2 text-left font-medium transition ${
                    activeSection === "Subscriptions & Billing"
                      ? "bg-[#0095D9] bg-opacity-10 text-[#0095D9]"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  Subscriptions & Billing
                </button>
              </div>
            </div>
          </div>

          {/* Right Content Area */}
          <div className='flex-1 p-8'>
            {activeSection === "Edit Profile" && (
              <>
                <div className='mb-6 flex items-center justify-end'>
                  <button
                    onClick={handleSave}
                    disabled={isUploadingImage}
                    className='rounded border-[2px] border-[#0095D9] bg-white px-4 py-2 text-[#0095D9] transition hover:bg-[#0095D9] hover:bg-opacity-10 disabled:opacity-50'
                  >
                    {isUploadingImage ? "Saving..." : "Save Changes"}
                  </button>
                </div>

                <div className='mt-6 flex flex-col border-b pb-6 md:flex-row'>
                  <div className='flex w-full flex-col items-center md:items-start'>
                    {/* Image Section */}
                    <div className='flex items-center gap-4'>
                      <div className='relative'>
                        {imagePreviewUrl ? (
                          <img
                            src={imagePreviewUrl}
                            alt='Profile'
                            className='h-[156px] w-[156px] rounded-full border border-gray-200 object-cover'
                          />
                        ) : (
                          <div className='flex h-[156px] w-[156px] items-center justify-center rounded-full border border-gray-200 bg-gradient-to-br from-[#0095D9] to-[#0075a9] text-4xl font-semibold text-white'>
                            {profile.businessName
                              ? profile.businessName.charAt(0).toUpperCase()
                              : "F"}
                          </div>
                        )}
                        <label
                          htmlFor='profile-image-upload'
                          className='absolute bottom-0 right-0 z-10 cursor-pointer rounded-full bg-[#0095D9] p-2 text-white shadow-lg transition hover:bg-[#0075a9]'
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className='h-5 w-5'
                            viewBox='0 0 20 20'
                            fill='currentColor'
                          >
                            <path
                              fillRule='evenodd'
                              d='M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z'
                              clipRule='evenodd'
                            />
                          </svg>
                        </label>
                        <input
                          ref={fileInputRef}
                          id='profile-image-upload'
                          type='file'
                          accept='image/*'
                          className='hidden'
                          onChange={handleImageUpload}
                        />
                      </div>
                      <div className='flex flex-col'>
                        <h2 className='text-2xl font-semibold'>
                          {profile.businessName || "Facility Name"}
                        </h2>
                        <p className='text-sm text-gray-500'>
                          {profile.emailId}
                        </p>
                      </div>
                    </div>

                    {/* Form Fields */}
                    <div className='mt-8 grid w-full grid-cols-1 gap-6 md:grid-cols-2'>
                      <div className='w-full'>
                        <label className='text-sm text-gray-500'>
                          Business Name
                        </label>
                        <input
                          name='businessName'
                          type='text'
                          value={profile.businessName}
                          onChange={handleChange}
                          placeholder='Entity Name'
                          className='mt-1 w-full rounded border px-3 py-2 text-sm'
                        />
                      </div>
                      <div className='w-full'>
                        <label className='text-sm text-gray-500'>
                          Owner Full Name
                        </label>
                        <input
                          name='fullName'
                          type='text'
                          value={profile.fullName}
                          onChange={handleChange}
                          placeholder='Owner Name'
                          className='mt-1 w-full rounded border px-3 py-2 text-sm'
                        />
                      </div>
                      <div className='w-full'>
                        <label className='text-sm text-gray-500'>Email</label>
                        <input
                          name='emailId'
                          type='email'
                          value={profile.emailId}
                          onChange={handleChange}
                          className='mt-1 w-full cursor-not-allowed rounded border bg-gray-50 px-3 py-2 text-sm'
                          disabled // Email usually not editable directly
                        />
                        <p className='mt-1 text-xs text-red-500'>
                          Email cannot be changed.
                        </p>
                      </div>
                      <div className='w-full'>
                        <label className='text-sm text-gray-500'>
                          Mobile Number
                        </label>
                        <input
                          name='mobileNumber'
                          type='number'
                          value={profile.mobileNumber}
                          onChange={handleChange}
                          className='mt-1 w-full rounded border px-3 py-2 text-sm'
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Sections */}
                <div className='border-b py-6'>
                  <h3 className='mb-3 text-lg font-semibold'>
                    Address Details
                  </h3>
                  <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                    <textarea
                      name='addressLineNo1'
                      value={profile.addressLineNo1}
                      onChange={handleChange}
                      className='w-full rounded border px-3 py-2 text-sm text-gray-600'
                      placeholder='Address Line 1'
                    />
                    <textarea
                      name='addressLineNo2'
                      value={profile.addressLineNo2}
                      onChange={handleChange}
                      className='w-full rounded border px-3 py-2 text-sm text-gray-600'
                      placeholder='Address Line 2'
                    />
                    <input
                      name='cityDistrict'
                      value={profile.cityDistrict}
                      onChange={handleChange}
                      className='w-full rounded border px-3 py-2 text-sm text-gray-600'
                      placeholder='City / District'
                    />
                    <input
                      name='state'
                      value={profile.state}
                      onChange={handleChange}
                      className='w-full rounded border px-3 py-2 text-sm text-gray-600'
                      placeholder='State'
                    />
                    <input
                      name='pincode'
                      value={profile.pincode}
                      onChange={handleChange}
                      className='w-full rounded border px-3 py-2 text-sm text-gray-600'
                      placeholder='Pincode'
                    />
                  </div>
                </div>

                <div className='border-b py-6'>
                  <h3 className='mb-3 text-lg font-semibold'>Legal Details</h3>
                  <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                    <div>
                      <label className='text-sm text-gray-500'>
                        GST Number
                      </label>
                      <input
                        name='gstNo'
                        value={profile.gstNo}
                        onChange={handleChange}
                        className='w-full rounded border px-3 py-2 text-sm text-gray-600'
                      />
                    </div>
                    <div>
                      <label className='text-sm text-gray-500'>
                        PAN Number
                      </label>
                      <input
                        name='panNo'
                        value={profile.panNo}
                        onChange={handleChange}
                        className='w-full rounded border px-3 py-2 text-sm text-gray-600'
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Render other sections */}
            {activeSection === "Account" && (
              <AccountSettings
                doctor={profile}
                emailVerificationRequired={
                  profile.emailVerificationRequired || false
                }
                setEmailVerificationRequired={(val) =>
                  setProfile((prev) => ({
                    ...prev,
                    emailVerificationRequired: val,
                  }))
                }
              />
            )}
            {activeSection === "Privacy" && (
              <PrivacySettings
                doctor={profile}
                onUpdate={(data) =>
                  setProfile((prev) => ({ ...prev, ...data }))
                }
              />
            )}
            {activeSection === "Email & Notifications" && (
              <EmailNotificationsSettings
                emailNotifications={emailNotifications}
                setEmailNotifications={setEmailNotifications}
              />
            )}
            {/* Languages section - commented out for now
            {activeSection === "Languages" && (
              <LanguagesSettings
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
              />
            )}
            */}
            {activeSection === "Subscriptions & Billing" && (
              <SubscriptionsBillingSettings />
            )}

            {/* Fallback for unmapped sections */}
            {![
              "Edit Profile",
              "Account",
              "Privacy",
              "Email & Notifications",
              // "Languages",
              "Subscriptions & Billing",
            ].includes(activeSection) && (
              <div className='py-12 text-center text-gray-500'>
                Section {activeSection} coming soon...
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
